import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPortalData, clientSendMessage, getPortalGoals, addPortalGoal, getPortalBreakthroughs, getPortalProfile, updatePortalProfile, changePortalPassword, updatePortalIntentions, validateAccessCode, PORTAL_BASE_URL } from '../api/client';
import LoadingLeaf from '../components/LoadingLeaf';

const timezoneOptions = typeof Intl.supportedValuesOf === 'function'
    ? Intl.supportedValuesOf('timeZone')
    : [Intl.DateTimeFormat().resolvedOptions().timeZone];

export default function ClientPortal() {
    const { accessCode } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [recording, setRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState('');
    const [goals, setGoals] = useState([]);
    const [breakthroughs, setBreakthroughs] = useState([]);
    const [newGoalText, setNewGoalText] = useState('');
    const [addingGoal, setAddingGoal] = useState(false);
    const [activeTab, setActiveTab] = useState('home');
    const [profile, setProfile] = useState(null);
    const [profileForm, setProfileForm] = useState({ name: '', email: '', timezone: '', username: '' });
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileMsg, setProfileMsg] = useState('');
    const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordMsg, setPasswordMsg] = useState('');
    const [intentionsForm, setIntentionsForm] = useState([]);
    const [intentionsSaving, setIntentionsSaving] = useState(false);
    const [intentionsMsg, setIntentionsMsg] = useState('');
    const messagesEndRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);

    const clientId = localStorage.getItem('portal_client_id');
    const clientName = localStorage.getItem('portal_client_name');
    const portalToken = localStorage.getItem('portal_token');

    useEffect(() => {
        if (!clientId) { navigate(`/portal/${accessCode}`); return; }
        fetchAll();
        const interval = setInterval(fetchData, 15000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [data?.messages]);

    const fetchAll = async () => {
        await Promise.all([fetchData(), fetchGoals(), fetchBreakthroughs(), fetchProfile()]);
    };

    const fetchProfile = async () => {
        try {
            const res = await getPortalProfile(clientId, portalToken);
            if (res.detail) return;
            setProfile(res);
            setProfileForm({
                name: res.name || '',
                email: res.email || '',
                timezone: res.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                username: res.username || '',
            });
            if (res.intentions?.length) {
                setIntentionsForm(res.intentions.map(i => ({ question: i.question, hint: i.hint, answer: i.answer || '' })));
            } else {
                // Never answered (skipped at first login) — offer the coach's
                // current questions so they can fill them in here.
                const v = await validateAccessCode(accessCode);
                setIntentionsForm((v?.questions || []).map(q => ({ question: q.question, hint: q.hint, answer: '' })));
            }
        } catch {}
    };

    const handleSaveIntentions = async () => {
        setIntentionsSaving(true);
        setIntentionsMsg('');
        try {
            const res = await updatePortalIntentions(clientId, portalToken, intentionsForm);
            setIntentionsMsg(res.detail ? 'Could not save changes.' : 'Saved.');
        } catch {
            setIntentionsMsg('Could not save changes.');
        } finally {
            setIntentionsSaving(false);
        }
    };

    const handleSaveProfile = async () => {
        setProfileSaving(true);
        setProfileMsg('');
        try {
            const res = await updatePortalProfile(clientId, portalToken, profileForm);
            if (res.detail) {
                setProfileMsg(res.detail);
            } else {
                setProfileMsg('Saved.');
                await fetchProfile();
            }
        } catch {
            setProfileMsg('Could not save changes.');
        } finally {
            setProfileSaving(false);
        }
    };

    const handleChangePassword = async () => {
        setPasswordMsg('');
        if (!passwordForm.new_password || passwordForm.new_password !== passwordForm.confirm_password) {
            setPasswordMsg('New passwords do not match.');
            return;
        }
        setPasswordSaving(true);
        try {
            const res = await changePortalPassword(clientId, portalToken, {
                current_password: passwordForm.current_password,
                new_password: passwordForm.new_password,
            });
            if (res.detail) {
                setPasswordMsg(res.detail);
            } else {
                setPasswordMsg('Password updated.');
                setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
            }
        } catch {
            setPasswordMsg('Could not update password.');
        } finally {
            setPasswordSaving(false);
        }
    };

    const fetchData = async () => {
        try {
            const res = await getPortalData(clientId, portalToken);
            if (res.detail) { navigate(`/portal/${accessCode}`); return; }
            setData(res);
        } catch (err) {
            setError('Could not load your portal.');
        } finally {
            setLoading(false);
        }
    };

    const fetchGoals = async () => {
        try {
            const res = await getPortalGoals(clientId, portalToken);
            if (Array.isArray(res)) setGoals(res);
        } catch {}
    };

    const fetchBreakthroughs = async () => {
        try {
            const res = await getPortalBreakthroughs(clientId, portalToken);
            if (Array.isArray(res)) setBreakthroughs(res);
        } catch {}
    };

    const handleAddGoal = async () => {
        if (!newGoalText.trim() || addingGoal) return;
        setAddingGoal(true);
        try {
            const res = await addPortalGoal(clientId, portalToken, newGoalText.trim());
            if (res.id) setGoals(prev => [res, ...prev]);
            setNewGoalText('');
        } catch {
            setError('Could not add goal.');
        } finally {
            setAddingGoal(false);
        }
    };

    const handleSendText = async () => {
        if (!message.trim()) return;
        setSending(true);
        try {
            await clientSendMessage(clientId, portalToken, {
                client_id: clientId,
                message_type: 'text',
                content: message.trim(),
            });
            setMessage('');
            await fetchData();
        } catch (err) {
            setError('Could not send message.');
        } finally {
            setSending(false);
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            chunksRef.current = [];
            const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg', 'audio/mp4']
                .find(t => MediaRecorder.isTypeSupported(t)) || 'audio/webm';
            mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
            mediaRecorderRef.current.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mimeType });
                setAudioBlob(blob);
                setAudioUrl(URL.createObjectURL(blob));
                stream.getTracks().forEach(t => t.stop());
            };
            mediaRecorderRef.current.start(1000);
            setRecordingTime(0);
            timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
            setRecording(true);
        } catch (err) {
            setError('Microphone access denied.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current) mediaRecorderRef.current.stop();
        if (timerRef.current) clearInterval(timerRef.current);
        setRecording(false);
    };

    const sendVoiceMessage = async () => {
        if (!audioBlob) return;
        setSending(true);
        try {
            const formData = new FormData();
            const ext = audioBlob.type.includes('mp4') ? 'mp4' : audioBlob.type.includes('ogg') ? 'ogg' : 'webm';
            formData.append('file', audioBlob, `voice_${Date.now()}.${ext}`);

            const uploadRes = await fetch(
                `${process.env.REACT_APP_API_URL}/portal/${clientId}/upload-voice`,
                { method: 'POST', headers: { 'Authorization': `Bearer ${portalToken}` }, body: formData }
            );
            if (!uploadRes.ok) throw new Error('Upload failed');
            const uploadData = await uploadRes.json();

            await clientSendMessage(clientId, portalToken, {
                client_id: clientId,
                message_type: 'voice',
                audio_url: uploadData.url,
                duration_seconds: recordingTime,
                transcription: uploadData.transcription || null,
            });

            setAudioBlob(null);
            setAudioUrl('');
            setRecordingTime(0);
            await fetchData();
        } catch (err) {
            setError('Could not send voice message: ' + err.message);
        } finally {
            setSending(false);
        }
    };

    const cancelVoice = () => {
        setAudioBlob(null);
        setAudioUrl('');
        setRecordingTime(0);
    };

    const handleLogout = () => {
        localStorage.removeItem('portal_client_id');
        localStorage.removeItem('portal_access_code');
        localStorage.removeItem('portal_client_name');
        localStorage.removeItem('portal_token');
        navigate(`/portal/${accessCode}`);
    };

    const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

    const formatMessageTime = (ts) => {
        if (!ts) return '';
        const d = new Date(ts.replace(' ', 'T'));
        if (isNaN(d)) return '';
        return d.toLocaleDateString('default', { month: 'short', day: 'numeric' }) + ' · ' +
            d.toLocaleTimeString('default', { hour: 'numeric', minute: '2-digit' });
    };

    const coachGoals = goals.filter(g => g.source === 'coach');
    const myGoals = goals.filter(g => g.source === 'client');

    const goalDotColor = (status) => {
        if (status === 'completed') return '#5C7A6B';
        if (status === 'paused') return '#A8B8B2';
        return '#C4956A';
    };

    if (loading) return (
        <div style={styles.screen}><LoadingLeaf fullScreen size={72} label="Loading your portal…" /></div>
    );

    return (
        <div style={styles.screen}>
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <div style={styles.logo}>Transformation Companion</div>
                    <div style={styles.headerSub}>
                        Welcome, {clientName} · <span style={{ fontSize: '11px', opacity: 0.7 }}>
                            Return anytime at {PORTAL_BASE_URL}/client-login
                        </span>
                    </div>
                </div>
                <button style={styles.logoutBtn} onClick={handleLogout}>Sign out</button>
            </div>

            <div style={styles.tabBar}>
                {[
                    { id: 'home', label: 'Home' },
                    { id: 'info', label: 'My Info' },
                    { id: 'settings', label: 'Settings' },
                ].map(tab => (
                    <button
                        key={tab.id}
                        style={{ ...styles.tabBtn, ...(activeTab === tab.id ? styles.tabBtnActive : {}) }}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'info' && (
                <div style={styles.content}>
                    {error && <div style={styles.errorAlert}>{error}</div>}
                    <div style={styles.card}>
                        <div style={styles.sectionLabel}>My info</div>
                        <div style={styles.infoRow}><div style={styles.infoLabel}>Name</div><div style={styles.infoValue}>{profile?.name || '—'}</div></div>
                        <div style={styles.infoRow}><div style={styles.infoLabel}>Email</div><div style={styles.infoValue}>{profile?.email || '—'}</div></div>
                        <div style={styles.infoRow}><div style={styles.infoLabel}>Username</div><div style={styles.infoValue}>{profile?.username || '—'}</div></div>
                        <div style={styles.infoRow}><div style={styles.infoLabel}>Timezone</div><div style={styles.infoValue}>{profile?.timezone || '—'}</div></div>
                    </div>
                    {intentionsForm.length > 0 && (
                        <div style={styles.card}>
                            <div style={styles.sectionLabel}>My intentions</div>
                            {intentionsForm.map((item, i) => (
                                <div key={i} style={{ marginBottom: '14px' }}>
                                    <div style={styles.intentionQuestion}>{item.question}</div>
                                    {item.hint && <div style={styles.intentionHint}>{item.hint}</div>}
                                    <textarea
                                        style={{ ...styles.input, minHeight: '64px', resize: 'vertical' }}
                                        value={item.answer}
                                        onChange={e => setIntentionsForm(f => f.map((x, k) => (k === i ? { ...x, answer: e.target.value } : x)))}
                                    />
                                </div>
                            ))}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <button style={{ ...styles.saveBtn, opacity: intentionsSaving ? 0.6 : 1 }} onClick={handleSaveIntentions} disabled={intentionsSaving}>
                                    {intentionsSaving ? 'Saving...' : 'Save'}
                                </button>
                                {intentionsMsg && <span style={styles.emptyHint}>{intentionsMsg}</span>}
                                <span style={{ ...styles.emptyHint, marginLeft: 'auto' }}>🔒 Only you and your coach see this</span>
                            </div>
                        </div>
                    )}
                    {profile?.intake_background && (
                        <div style={styles.card}>
                            <div style={styles.sectionLabel}>My intake responses</div>
                            <div style={styles.intakeText}>{profile.intake_background}</div>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'settings' && (
                <div style={styles.content}>
                    {error && <div style={styles.errorAlert}>{error}</div>}
                    <div style={styles.card}>
                        <div style={styles.sectionLabel}>Edit details</div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Name</label>
                            <input style={styles.input} value={profileForm.name} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))} />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Email</label>
                            <input type="email" style={styles.input} value={profileForm.email} onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))} />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Username</label>
                            <input style={styles.input} value={profileForm.username} onChange={e => setProfileForm(f => ({ ...f, username: e.target.value }))} />
                            <div style={{ fontSize: '12px', color: '#8C9E98', marginTop: '6px' }}>
                                You can use this to sign in later instead of your access code.
                            </div>
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Timezone</label>
                            <select style={styles.input} value={profileForm.timezone} onChange={e => setProfileForm(f => ({ ...f, timezone: e.target.value }))}>
                                {timezoneOptions.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                            </select>
                        </div>
                        {profileMsg && <div style={styles.saveMsg}>{profileMsg}</div>}
                        <button style={{ ...styles.saveBtn, opacity: profileSaving ? 0.7 : 1 }} onClick={handleSaveProfile} disabled={profileSaving}>
                            {profileSaving ? 'Saving...' : 'Save changes'}
                        </button>
                    </div>
                    <div style={styles.card}>
                        <div style={styles.sectionLabel}>Change password</div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Current password</label>
                            <input type="password" style={styles.input} value={passwordForm.current_password} onChange={e => setPasswordForm(f => ({ ...f, current_password: e.target.value }))} />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>New password</label>
                            <input type="password" style={styles.input} value={passwordForm.new_password} onChange={e => setPasswordForm(f => ({ ...f, new_password: e.target.value }))} />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Confirm new password</label>
                            <input type="password" style={styles.input} value={passwordForm.confirm_password} onChange={e => setPasswordForm(f => ({ ...f, confirm_password: e.target.value }))} />
                        </div>
                        {passwordMsg && <div style={styles.saveMsg}>{passwordMsg}</div>}
                        <button style={{ ...styles.saveBtn, opacity: passwordSaving ? 0.7 : 1 }} onClick={handleChangePassword} disabled={passwordSaving}>
                            {passwordSaving ? 'Updating...' : 'Update password'}
                        </button>
                    </div>
                </div>
            )}

            {activeTab === 'home' && (
            <div style={styles.content}>
                {error && <div style={styles.errorAlert}>{error}</div>}

                {/* Focus Areas — coach-set goals */}
                {coachGoals.length > 0 && (
                    <div style={styles.card}>
                        <div style={styles.sectionLabel}>Your focus areas</div>
                        {coachGoals.map(g => (
                            <div key={g.id} style={styles.goalItem}>
                                <div style={{ ...styles.goalDot, background: goalDotColor(g.status) }} />
                                <div style={{
                                    ...styles.goalText,
                                    textDecoration: g.status === 'completed' ? 'line-through' : 'none',
                                    opacity: g.status === 'paused' ? 0.5 : 1,
                                }}>
                                    {g.text}
                                </div>
                                {g.status === 'completed' && <span style={{ fontSize: '13px', color: '#5C7A6B' }}>✓</span>}
                            </div>
                        ))}
                    </div>
                )}

                {/* My Goals — client-submitted */}
                <div style={styles.card}>
                    <div style={styles.sectionLabel}>My goals</div>
                    {myGoals.length === 0 && (
                        <div style={styles.emptyHint}>Share your own goals with your coach by adding them here.</div>
                    )}
                    {myGoals.map(g => (
                        <div key={g.id} style={styles.goalItem}>
                            <div style={{ ...styles.goalDot, background: '#5C7A6B' }} />
                            <div style={styles.goalText}>{g.text}</div>
                        </div>
                    ))}
                    <div style={styles.addGoalRow}>
                        <input
                            style={styles.goalInput}
                            placeholder="What do you want to work toward?"
                            value={newGoalText}
                            onChange={e => setNewGoalText(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleAddGoal()}
                        />
                        <button style={styles.addGoalBtn} onClick={handleAddGoal} disabled={addingGoal}>
                            {addingGoal ? '...' : 'Add'}
                        </button>
                    </div>
                </div>

                {/* Breakthroughs */}
                {breakthroughs.length > 0 && (
                    <div style={styles.card}>
                        <div style={styles.sectionLabel}>Your breakthroughs</div>
                        {breakthroughs.slice(0, 3).map((bq, i) => (
                            <div key={i} style={styles.breakthroughCard}>
                                <div style={styles.breakthroughQuote}>"{bq.quote}"</div>
                                {bq.date && (
                                    <div style={styles.breakthroughDate}>
                                        {new Date(bq.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Messages */}
                <div style={styles.messagesCard}>
                    <div style={styles.messagesTitle}>Your conversation</div>
                    <div style={styles.messagesList}>
                        {data?.messages?.length === 0 && (
                            <div style={styles.emptyMessages}>No messages yet. Send your coach a message below.</div>
                        )}
                        {data?.messages?.map(msg => (
                            <div
                                key={msg.id}
                                style={{
                                    ...styles.messageBubble,
                                    ...(msg.sender_type === 'client' ? styles.messageBubbleClient : styles.messageBubbleCoach)
                                }}
                            >
                                <div style={styles.messageSender}>
                                    {msg.sender_type === 'client' ? 'You' : 'Your Coach'}
                                </div>
                                {msg.message_type === 'text' && (
                                    <div style={styles.messageText}>{msg.content}</div>
                                )}
                                {msg.message_type === 'voice' && (
                                    <div>
                                        <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '6px' }}>🎙 Voice message</div>
                                        <audio controls src={msg.audio_url} style={{ width: '100%', minWidth: '260px', display: 'block', borderRadius: '6px' }} />
                                        {msg.duration_seconds && (
                                            <div style={styles.audioDuration}>
                                                {Math.floor(msg.duration_seconds / 60)}:{String(msg.duration_seconds % 60).padStart(2, '0')}
                                            </div>
                                        )}
                                        {msg.transcription && (
                                            <div style={{ fontSize: '13px', fontStyle: 'italic', lineHeight: 1.5, marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.15)', opacity: 0.85 }}>
                                                "{msg.transcription}"
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div style={styles.messageTime}>{formatMessageTime(msg.created_at)}</div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>

                    <div style={styles.inputArea}>
                        {audioUrl ? (
                            <div style={styles.voicePreview}>
                                <audio controls src={audioUrl} style={styles.audioPlayer} />
                                <div style={styles.voicePreviewActions}>
                                    <button style={styles.sendVoiceBtn} onClick={sendVoiceMessage} disabled={sending}>
                                        {sending ? 'Sending...' : 'Send voice message'}
                                    </button>
                                    <button style={styles.cancelBtn} onClick={cancelVoice}>Cancel</button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <textarea
                                    style={styles.messageInput}
                                    placeholder="Type a message to your coach..."
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendText(); } }}
                                    rows={3}
                                />
                                <div style={styles.inputActions}>
                                    {!recording ? (
                                        <>
                                            <button style={styles.voiceBtn} onClick={startRecording}>🎙 Voice note</button>
                                            <button
                                                style={{ ...styles.sendBtn, opacity: sending ? 0.7 : 1 }}
                                                onClick={handleSendText}
                                                disabled={sending || !message.trim()}
                                            >
                                                {sending ? 'Sending...' : 'Send'}
                                            </button>
                                        </>
                                    ) : (
                                        <div style={styles.recordingBar}>
                                            <div style={styles.recordingDot} />
                                            <div style={styles.recordingTime}>{formatTime(recordingTime)}</div>
                                            <button style={styles.stopRecordBtn} onClick={stopRecording}>Stop recording</button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            )}
        </div>
    );
}

const styles = {
    screen: { minHeight: '100vh', background: '#F8F5F0', fontFamily: "'DM Sans','Segoe UI',sans-serif" },
    loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: '14px', color: '#8C9E98' },
    header: { background: '#1A2E28', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 },
    headerLeft: {},
    logo: { fontFamily: "'Lora',Georgia,serif", fontSize: '16px', fontWeight: '300', fontStyle: 'italic', color: '#E8CBA9', marginBottom: '2px' },
    headerSub: { fontSize: '12px', color: '#8C9E98' },
    logoutBtn: { background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '7px', color: 'rgba(255,255,255,0.5)', padding: '7px 14px', fontSize: '12.5px', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" },
    tabBar: { display: 'flex', gap: '4px', maxWidth: '680px', margin: '0 auto', padding: '16px 16px 0' },
    tabBtn: { padding: '9px 16px', borderRadius: '8px 8px 0 0', background: 'none', border: 'none', borderBottom: '2px solid transparent', color: '#8C9E98', fontFamily: "'DM Sans',sans-serif", fontSize: '13.5px', fontWeight: '500', cursor: 'pointer' },
    tabBtnActive: { color: '#1C2B26', borderBottom: '2px solid #5C7A6B' },
    content: { maxWidth: '680px', margin: '0 auto', padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: '14px' },
    errorAlert: { background: '#F5EAEA', color: '#B85C5C', padding: '12px 14px', borderRadius: '8px', fontSize: '13.5px' },
    card: { background: 'white', borderRadius: '12px', border: '1px solid #D8E0DC', padding: '20px' },
    infoRow: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #F0F0EE' },
    infoLabel: { fontSize: '13px', color: '#8C9E98' },
    infoValue: { fontSize: '13.5px', color: '#1C2B26', fontWeight: '500' },
    intentionQuestion: { fontSize: '14px', fontWeight: '600', color: '#1C2B26', marginBottom: '2px' },
    intentionHint: { fontSize: '12.5px', color: '#8C9E98', marginBottom: '6px' },
    intakeText: { fontSize: '13.5px', color: '#1C2B26', lineHeight: 1.7, whiteSpace: 'pre-wrap' },
    formGroup: { marginBottom: '16px' },
    label: { display: 'block', fontSize: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#8C9E98', marginBottom: '8px' },
    input: { width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid #D8E0DC', background: '#F8F5F0', fontFamily: "'DM Sans',sans-serif", fontSize: '14px', color: '#2D3A35', outline: 'none', boxSizing: 'border-box' },
    saveMsg: { fontSize: '13px', color: '#5C7A6B', marginBottom: '10px' },
    saveBtn: { padding: '11px 20px', borderRadius: '8px', background: '#5C7A6B', border: 'none', color: 'white', fontFamily: "'DM Sans',sans-serif", fontSize: '13.5px', fontWeight: '500', cursor: 'pointer' },
    sectionLabel: { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.09em', color: '#8C9E98', marginBottom: '14px' },
    goalItem: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
    goalDot: { width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0 },
    goalText: { fontSize: '14px', color: '#1C2B26', lineHeight: 1.5, flex: 1 },
    emptyHint: { fontSize: '13px', color: '#8C9E98', lineHeight: 1.6, marginBottom: '12px' },
    addGoalRow: { display: 'flex', gap: '8px', marginTop: '10px' },
    goalInput: { flex: 1, padding: '10px 13px', borderRadius: '8px', border: '1px solid #D8E0DC', background: '#F8F5F0', fontFamily: "'DM Sans',sans-serif", fontSize: '13.5px', color: '#1C2B26', outline: 'none' },
    addGoalBtn: { padding: '10px 16px', borderRadius: '8px', background: '#5C7A6B', border: 'none', color: 'white', fontFamily: "'DM Sans',sans-serif", fontSize: '13.5px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
    breakthroughCard: { borderLeft: '3px solid #C4956A', paddingLeft: '14px', marginBottom: '14px' },
    breakthroughQuote: { fontFamily: "'Lora',Georgia,serif", fontSize: '14px', fontStyle: 'italic', color: '#1C2B26', lineHeight: 1.7, marginBottom: '6px' },
    breakthroughDate: { fontSize: '12px', color: '#8C9E98' },
    messagesCard: { background: 'white', borderRadius: '12px', border: '1px solid #D8E0DC', overflow: 'hidden' },
    messagesTitle: { padding: '16px 20px', borderBottom: '1px solid #D8E0DC', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.09em', color: '#8C9E98' },
    messagesList: { padding: '16px', maxHeight: '480px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' },
    emptyMessages: { textAlign: 'center', color: '#8C9E98', fontSize: '14px', padding: '32px 0' },
    messageBubble: { maxWidth: '85%', minWidth: '280px', borderRadius: '12px', padding: '12px 14px' },
    messageBubbleClient: { background: '#5C7A6B', color: 'white', alignSelf: 'flex-end', borderBottomRightRadius: '4px' },
    messageBubbleCoach: { background: '#F0F4F2', color: '#2D3A35', alignSelf: 'flex-start', borderBottomLeftRadius: '4px' },
    messageSender: { fontSize: '11px', fontWeight: '500', opacity: 0.7, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' },
    messageText: { fontSize: '14px', lineHeight: 1.6 },
    messageTime: { fontSize: '11px', opacity: 0.6, marginTop: '6px', textAlign: 'right' },
    audioDuration: { fontSize: '11px', opacity: 0.7, marginTop: '4px' },
    audioPlayer: { width: '100%', borderRadius: '6px' },
    inputArea: { padding: '16px', borderTop: '1px solid #D8E0DC' },
    messageInput: { width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #D8E0DC', background: '#F8F5F0', fontFamily: "'DM Sans',sans-serif", fontSize: '14px', color: '#2D3A35', outline: 'none', resize: 'none', boxSizing: 'border-box', marginBottom: '10px' },
    inputActions: { display: 'flex', gap: '8px', justifyContent: 'flex-end' },
    voiceBtn: { padding: '10px 16px', borderRadius: '8px', background: 'none', border: '1px solid #D8E0DC', color: '#4A5A54', fontFamily: "'DM Sans',sans-serif", fontSize: '13px', cursor: 'pointer' },
    sendBtn: { padding: '10px 20px', borderRadius: '8px', background: '#5C7A6B', border: 'none', color: 'white', fontFamily: "'DM Sans',sans-serif", fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
    recordingBar: { display: 'flex', alignItems: 'center', gap: '12px', width: '100%' },
    recordingDot: { width: '10px', height: '10px', borderRadius: '50%', background: '#C25E5E' },
    recordingTime: { fontFamily: 'monospace', fontSize: '16px', color: '#C25E5E', flex: 1 },
    stopRecordBtn: { padding: '10px 16px', borderRadius: '8px', background: '#C25E5E', border: 'none', color: 'white', fontFamily: "'DM Sans',sans-serif", fontSize: '13px', cursor: 'pointer' },
    voicePreview: { marginBottom: '8px' },
    voicePreviewActions: { display: 'flex', gap: '8px', marginTop: '10px' },
    sendVoiceBtn: { padding: '10px 18px', borderRadius: '8px', background: '#5C7A6B', border: 'none', color: 'white', fontFamily: "'DM Sans',sans-serif", fontSize: '13px', cursor: 'pointer' },
    cancelBtn: { padding: '10px 16px', borderRadius: '8px', background: 'none', border: '1px solid #D8E0DC', color: '#4A5A54', fontFamily: "'DM Sans',sans-serif", fontSize: '13px', cursor: 'pointer' },
};
